import { FormProvider, useForm } from 'react-hook-form';
import React, { FC, Fragment, useState } from 'react';
import { Box, Button } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';

import styled from '../../../../../styled';
import { DetailingCheckBoxes } from '../detailingCheckBoxes';

import { AboutFormData } from './types';
import { Info } from './general/info';
import { Errors } from './errors';
import { HeatingForm } from './heating';
import { Ventilation } from './ventilation';
import { DetailingCheckBoxesV2025 } from './detailingSelect';
import { DeleteExistingDataDialog } from './deleteExistingDataDialog';

const ButtonWrapper = styled(Box)`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`;

interface Props {
  defaultValues: AboutFormData;
  onSubmit: (values: AboutFormData) => void;
}

export const AboutForm: FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();
  const methods = useForm<AboutFormData>({
    defaultValues,
    mode: 'onBlur'
  });

  const errors = methods.formState.errors;

  const submitCaption = !defaultValues.registreringId
    ? t('register.actions.startDetailing')
    : t('register.actions.next');

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <Box display="flex" flexDirection="column" gap={3}>
          <Info control={methods.control} setValue={methods.setValue} />
          <HeatingForm control={methods.control} />
          <Ventilation control={methods.control} />

          <DetailingCheckBoxes />

          <Errors errors={errors} />
          <ButtonWrapper my={3}>
            <Button type="submit">{submitCaption}</Button>
          </ButtonWrapper>
        </Box>
      </form>
    </FormProvider>
  );
};

export const AboutFormV2025: FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const methods = useForm<AboutFormData>({
    defaultValues,
    mode: 'onBlur'
  });

  const errors = methods.formState.errors;

  const submitCaption = !defaultValues.registreringId
    ? t('register.actions.startDetailing')
    : t('register.actions.next');

  const onFormSubmit = (data: AboutFormData) => {
    const changedDetaljeringssteg = methods.formState.dirtyFields.detaljeringssteg;
    const detaljeringssteg = data.detaljeringssteg;

    // This might delete data. Prompt for confirmation if any of the detailing checkboxes has been unchecked.
    // Checks if any checkbox was changed from true to false
    const displayDialog =
      (!!changedDetaljeringssteg?.bygningsform && !detaljeringssteg?.bygningsform) ||
      (!!changedDetaljeringssteg?.gulv && !detaljeringssteg?.gulv) ||
      (!!changedDetaljeringssteg?.takkonstruksjon && !detaljeringssteg?.takkonstruksjon) ||
      (!!changedDetaljeringssteg?.ytterdorer && !detaljeringssteg?.ytterdorer) ||
      (!!changedDetaljeringssteg?.energibruk && !detaljeringssteg?.energibruk) ||
      (!!changedDetaljeringssteg?.lekkasjetall && !detaljeringssteg?.lekkasjetall) ||
      (!!changedDetaljeringssteg?.solceller && !detaljeringssteg?.solceller);

    if (displayDialog) {
      setIsDeleteDialogOpen(displayDialog);
      return;
    }
    onSubmit(data);
  };

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onFormSubmit)} noValidate>
          <Box display="flex" flexDirection="column" gap={3}>
            <Info control={methods.control} setValue={methods.setValue} />
            <HeatingForm control={methods.control} />
            <Ventilation control={methods.control} />
            <DetailingCheckBoxesV2025 />

            <Errors errors={errors} />
            <ButtonWrapper my={3}>
              <Button type="submit">{submitCaption}</Button>
            </ButtonWrapper>
          </Box>
        </form>
      </FormProvider>

      <DeleteExistingDataDialog
        isOpen={isDeleteDialogOpen}
        onConfirm={() => onSubmit(methods.getValues())}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />
    </Fragment>
  );
};
